export type SearchBounds = { north: number; south: number; east: number; west: number }

type BoundsState = { mapSearchActive: boolean; mapVisible: boolean; mapBounds: SearchBounds | null; mobileSheetActive?: boolean }

/**
 * Are the search-as-move bounds actually APPLIED to the results right now?
 * Every bounds consumer (grid, totals, price histogram, both renderer
 * queries) gates on the same three flags — a hidden map suspends the area
 * without clearing it. The renderers' framing must agree with that: a map
 * showing an area the list is not filtered by (or the reverse) is the
 * inconsistency codex r4 flagged when a visitor hides the map with bounds
 * and then enters the sheet. On the list layout a renderer only mounts while
 * the map is visible, so this changes nothing there.
 */
export function boundsApplied(state: BoundsState): boolean {
  return state.mapSearchActive && state.mapVisible && !!state.mapBounds
}

/**
 * Are the bounds actually FILTERING the results right now? Same as
 * `boundsApplied`, minus the window where the mobile sheet suspends
 * search-as-move (#856).
 *
 * The two questions are genuinely different, which is why this is not just
 * folded into `boundsApplied`. On the sheet the renderer still SEEDS its camera
 * from the stored bounds so the visitor's area survives the rotate — but the
 * grid must not stay filtered by them, because the sheet hides both the
 * search-as-move checkbox and the "Search this area" pill and never refreshes
 * the bounds on moveend. Leaving them applied stranded the visitor: panning the
 * map changed nothing and there was no control to clear it.
 */
export function boundsFiltering(state: BoundsState): boolean {
  return boundsApplied(state) && !state.mobileSheetActive
}

/**
 * #856 (codex r1): a map renderer mounting while search-as-move bounds are
 * APPLIED must open on those bounds, not on the default US camera. The
 * mobile results sheet remounts the renderer under a different parent when
 * the viewport crosses the breakpoint, and the marker effect deliberately
 * skips its fit while applied bounds exist (they would be narrowed by the
 * frame) — so without this seed the visitor's own map area was lost on
 * rotate. Returns null when there is nothing to seed or the bounds are
 * degenerate.
 */
export function initialSearchBounds(state: BoundsState): SearchBounds | null {
  const b = state.mapBounds
  if (!boundsApplied(state) || !b) return null
  if (![b.north, b.south, b.east, b.west].every(Number.isFinite)) return null
  if (b.north <= b.south) return null
  return b
}
